import { inject, Injectable, signal } from '@angular/core';
import { JourneyService } from './journey.service';

/**
 * State machine of the onboarding popup.
 *
 * It owns no data of its own: whether to open at all is journey state, and persisting
 * "ne plus montrer" belongs to `JourneyService`.
 *
 * The popup shows the journey recap and the next action, so long as the journey is
 * unfinished. The intro video is a step of that journey like any other, watched from the
 * Getting Started page — the popup never plays it.
 */

/** Delay before the popup appears, so it does not fight the page transition. */
const POPUP_DELAY_MS = 1000;

@Injectable({
  providedIn: 'root',
})
export class OnboardingService {
  private journeyService = inject(JourneyService);

  readonly visible = signal(false);

  /**
   * Kept in memory rather than in sessionStorage: a session flag survives logout in the same
   * tab, so the next user would silently lose their own popup. Same reasoning as
   * UpdateNoteService. AuthService.logout() calls reset().
   */
  private hasPromptedThisSession = false;

  /**
   * Opens the popup at most once per session, and reports whether it did. The caller loads
   * the journey first; one that failed to load leaves every flag false, so nothing opens.
   */
  promptIfNeeded(): boolean {
    if (this.hasPromptedThisSession) {
      return false;
    }
    this.hasPromptedThisSession = true;

    if (!this.journeyService.showPopup() || this.journeyService.isComplete()) {
      return false;
    }

    setTimeout(() => this.open(), POPUP_DELAY_MS);
    return true;
  }

  open(): void {
    this.visible.set(true);
  }

  close(): void {
    this.visible.set(false);
  }

  /** Called on logout so the next user in the same tab gets their own popup. */
  reset(): void {
    this.hasPromptedThisSession = false;
    this.visible.set(false);
  }
}
